import classNames from 'classnames'
import { FunctionComponent, useState } from 'react'
import { Icon, IconType } from '../..'

type CollaborationTipsAudience = 'brand' | 'creator'

interface IProps {
  audience: CollaborationTipsAudience
  className?: string
}

const SUPPORT_EMAIL = 'hello@useclip.com'

const renderTip = (tip: string) => {
  if (!tip.includes(SUPPORT_EMAIL)) {
    return tip
  }

  const [before, after] = tip.split(SUPPORT_EMAIL)

  return (
    <>
      {before}
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className='font-medium text-blue-600 underline'
      >
        {SUPPORT_EMAIL}
      </a>
      {after}
    </>
  )
}

const TIPS: Record<CollaborationTipsAudience, string[]> = {
  brand: [
    'Message the creator now that they are approved to make sure they understand the brief.',
    'Send the product within 2 weeks of approving the creator.',
    'If you approved the creator more than 30 days after they applied, check they are still available before shipping.',
    'If the creator is unresponsive, do not ship the product.',
    'Clip is not responsible for products exchanged between brands and creators.',
    'Refunds cannot be issued more than 30 days after purchase.',
    'To change a campaign, remove a creator, or report an issue, email us at hello@useclip.com.',
  ],
  creator: [
    'Message the brand now that you are approved to confirm you understand the brief.',
    'Expect your product to be sent within 2 weeks of being approved.',
    'If you were approved more than 30 days after applying, the brand may check you are still available before shipping - so reply promptly.',
    'Stay responsive: if the brand cannot reach you, they may hold off on shipping the product.',
    'Confirm your shipping details with the brand so your product arrives smoothly.',
    'Keep all messages and payments on Clip; off-platform contact or payment is not allowed.',
    'For any issues or changes to the collaboration, email us at hello@useclip.com.',
  ],
}

const CollaborationTips: FunctionComponent<IProps> = ({
  audience,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(true)
  const tips = TIPS[audience]

  return (
    <div
      className={classNames(
        'rounded-lg border border-blue-100 bg-blue-50',
        className,
      )}
    >
      <button
        type='button'
        onClick={() => setIsOpen((open) => !open)}
        className='flex w-full items-center justify-between px-4 py-3 text-left'
        aria-expanded={isOpen}
      >
        <span className='flex items-center gap-2 text-sm font-semibold text-primary-900'>
          <Icon
            type={IconType.info}
            className='h-5 w-5 text-blue-600'
            aria-hidden='true'
          />
          Tips for a great collaboration
        </span>
        <Icon
          type={isOpen ? IconType.chevronUp : IconType.chevronDown}
          className='h-5 w-5 text-primary-500'
          aria-hidden='true'
        />
      </button>

      {isOpen && (
        <ul className='list-disc space-y-2 px-4 pb-4 pl-9 text-sm text-primary-700'>
          {tips.map((tip) => (
            <li key={tip}>{renderTip(tip)}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

export { CollaborationTips }
